// Mounts Tamagui for the app and maps the stored interface version (lib/theme.ts: dark, light or
// the accessible one) onto a Tamagui theme. The accessible version follows the device's colour
// scheme, so it has a light and a dark palette.
import { useSyncExternalStore } from "react";
import { TamaguiProvider } from "tamagui";
import { useTheme, type Theme } from "../lib/theme";
import { config } from "./config";

export type ThemeName = "dark" | "light" | "a11yLight" | "a11yDark";

const DARK_SCHEME = "(prefers-color-scheme: dark)";

function subscribeScheme(listener: () => void): () => void {
  const query = window.matchMedia(DARK_SCHEME);
  query.addEventListener("change", listener);
  return () => query.removeEventListener("change", listener);
}

const prefersDark = (): boolean => window.matchMedia(DARK_SCHEME).matches;

/** The Tamagui theme for an interface version and the device's colour scheme. */
export function themeName(theme: Theme, deviceDark: boolean): ThemeName {
  if (theme === "a11y") return deviceDark ? "a11yDark" : "a11yLight";
  return theme;
}

export function UIProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const theme = useTheme();
  const deviceDark = useSyncExternalStore(subscribeScheme, prefersDark, () => true);
  return (
    <TamaguiProvider config={config} defaultTheme={themeName(theme, deviceDark)}>
      {children}
    </TamaguiProvider>
  );
}
